import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { useTaxonomyStudioStore } from '~/store/taxonomy-studio.store';

import { fetchDraftActions } from '../services/taxonomy-read.service';
import { undoAction } from '../services/taxonomy-write.service';
import type { Release, WriteMeta, WriteResult } from '../types/taxonomy-studio.types';
import { readProblem } from '../utils/taxonomy-errors';

import { useTaxonomyErrors } from './use-taxonomy-errors';

/**
 * After any write: record the new draft version as ours (so the poll does not report
 * it as someone else's), patch the cached draft's version, and invalidate every
 * taxonomy read — numbered releases are immutable and HTTP-cached, so refetching them
 * costs nothing while the draft/checks/touched lists come back fresh.
 */
function useAfterWrite() {
  const qc = useQueryClient();
  return useCallback(
    (meta: WriteMeta) => {
      if (meta.draft) {
        useTaxonomyStudioStore.getState().setKnown(meta.draft.seq, meta.draft.draftVersion);
        const draftUpdate = meta.draft;
        qc.setQueryData<Release | null>(queryKeys.taxonomyStudio.draft(), (prev) =>
          prev?.seq === draftUpdate.seq
            ? { ...prev, draftVersion: draftUpdate.draftVersion }
            : prev,
        );
      }
      void qc.invalidateQueries({ queryKey: queryKeys.taxonomyStudio.all });
    },
    [qc],
  );
}

/**
 * Undo one action (POST draft/actions/{id}/undo). On 409 UNDO_CONFLICT the toast offers
 * "Revert these items instead", which opens the draft's change list searched for the first
 * conflicting item, where each change can be reverted on its own.
 */
export function useUndoAction() {
  const { t } = useTranslation('taxonomy-studio');
  const navigate = useNavigate();
  const afterWrite = useAfterWrite();
  const { notify } = useTaxonomyErrors();
  const mutation = useMutation({
    mutationFn: (actionId: string) => undoAction(actionId),
    onSuccess: (res) => {
      afterWrite(res);
      toast.success(t('toast.undone', { label: res.actionLabel ?? '' }));
    },
    onError: (err, actionId) => {
      const p = readProblem(err);
      if (p.code === 'UNDO_CONFLICT') {
        const first = p.conflicts[0];
        // Name what blocked the undo so the user knows which items to revert by hand.
        const names = p.conflicts
          .slice(0, 5)
          .map((c) => c.entityLabel || c.entityKey)
          .join(', ');
        toast.error(t('errors.title.UNDO_CONFLICT'), {
          id: `taxonomy-undo-conflict:${actionId}`,
          description: [
            p.detail || t('errors.detail.UNDO_CONFLICT'),
            names ? t('errors.conflictsList', { names }) : '',
          ]
            .filter(Boolean)
            .join(' '),
          duration: 12_000,
          action: {
            label: t('toast.revertInstead'),
            onClick: () => {
              void navigate({
                to: '/taxonomy/versions',
                search: {
                  seq: 'draft',
                  view: 'release',
                  ...(first ? { q: first.entityLabel || first.entityKey } : {}),
                },
              });
            },
          },
        });
        return;
      }
      if (p.code === 'ALREADY_UNDONE') {
        toast.info(t('toast.alreadyUndone'), { id: `taxonomy-undone:${actionId}` });
        return;
      }
      notify(err, {
        retry: () => {
          mutation.mutate(actionId);
        },
      });
    },
  });
  return mutation;
}

/**
 * Turns a write result into user feedback: invalidations plus a Sonner toast naming the
 * action with an Undo button (when the server returned an `action_id`).
 */
export function useWriteFeedback() {
  const { t } = useTranslation('taxonomy-studio');
  const afterWrite = useAfterWrite();
  const { mutate: undo } = useUndoAction();
  return useCallback(
    (meta: WriteMeta, fallbackLabel?: string) => {
      afterWrite(meta);
      const label = meta.actionLabel ?? fallbackLabel ?? t('toast.saved');
      const actionId = meta.actionId;
      toast.success(label, {
        duration: 8_000,
        ...(actionId
          ? {
              action: {
                label: t('toast.undo'),
                onClick: () => {
                  undo(actionId);
                },
              },
            }
          : {}),
      });
    },
    [afterWrite, t, undo],
  );
}

export interface TaxonomyMutationOptions<TVars, T> {
  onSuccess?: (res: WriteResult<T>, vars: TVars) => void;
  /** Return true when the error was handled (e.g. a 422 shown inline in a form). */
  onError?: (err: unknown, vars: TVars) => boolean;
  /**
   * For `If-Match` writes: on 412 STALE_ITEM open the "Someone changed this" dialog.
   * `label` names the item; `overwrite` returns the same variables without the revision,
   * so "Overwrite" re-sends the user's version over the other person's.
   */
  stale?: { label: (vars: TVars) => string; overwrite: (vars: TVars) => TVars };
  /** Readable names for bulk failures (`items[].key` → name). */
  itemNames?: (vars: TVars) => Record<string, string>;
}

/**
 * `useMutation` for any taxonomy write: success runs the shared feedback (undo toast,
 * invalidations); failures toast the problem `detail` unless the caller handles them.
 */
export function useTaxonomyMutation<TVars, T>(
  fn: (vars: TVars) => Promise<WriteResult<T>>,
  options: TaxonomyMutationOptions<TVars, T> = {},
) {
  const feedback = useWriteFeedback();
  const { notify } = useTaxonomyErrors();
  const { onSuccess, onError, stale, itemNames } = options;
  const mutation = useMutation({
    mutationFn: fn,
    onSuccess: (res, vars) => {
      feedback(res);
      onSuccess?.(res, vars);
    },
    onError: (err, vars) => {
      const p = readProblem(err);
      if (p.code === 'STALE_ITEM' && stale) {
        useTaxonomyStudioStore.getState().setStale({
          itemLabel: stale.label(vars),
          overwrite: () => {
            mutation.mutate(stale.overwrite(vars));
          },
        });
        return;
      }
      if (onError?.(err, vars)) return;
      notify(err, {
        retry: () => {
          mutation.mutate(vars);
        },
        itemNames: itemNames?.(vars),
      });
    },
  });
  return mutation;
}

/**
 * Ctrl/Cmd+Z: undo the newest of my own undoable actions in the draft. Returns a
 * handler for the layout's keydown listener (skips focus inside text fields so native
 * text undo still works).
 */
export function useUndoLatest() {
  const { t } = useTranslation('taxonomy-studio');
  const qc = useQueryClient();
  const { mutate: undo } = useUndoAction();
  const { notify } = useTaxonomyErrors();
  return useCallback(async () => {
    try {
      const actions = await qc.fetchQuery({
        queryKey: queryKeys.taxonomyStudio.actions(),
        queryFn: fetchDraftActions,
        staleTime: 0,
      });
      const latest = actions.find((a) => a.canUndo && !a.undoneAt);
      if (!latest) {
        toast.info(t('toast.nothingToUndo'));
        return;
      }
      undo(latest.id);
    } catch (err) {
      notify(err);
    }
  }, [notify, qc, t, undo]);
}
